import { selectLocalStore, type LocalStorePort } from '../../platform/local-store';
import type { CaptureStatus } from '../database.types';

/**
 * THE DURABLE CAPTURE DRAFT — M4 / S-04-01, under D-002 and D-019.
 *
 * Until this slice, every unsaved Capture lived in React memory alone. An
 * Android low-memory kill therefore took the typed note, the delivered
 * transcript, the edited transcript, the stable `client_capture_uuid` and the
 * frozen payload of an ambiguous Save with it, and Crewlink reopened showing
 * no sign that any of it had ever existed. This module is the device-local
 * store that makes that work survive, and the recovery card is how it is
 * offered back.
 *
 * WHAT IS PERSISTED IS EXACTLY WHAT D-019 (J-2) APPROVED, and no more:
 *
 *   typed note / delivered / edited transcript  ·  selected Capture status
 *   the Project identity needed to restore context  ·  the stable
 *   `client_capture_uuid`  ·  the save-reconciliation metadata an ambiguous
 *   Save needs  ·  a boolean saying an unresolved recording existed
 *
 * WHAT IS DELIBERATELY NEVER PERSISTED — the list is the point of the module:
 *
 *   NO AUDIO of any kind. No Blob, no chunk, no filename, no path. D-010's
 *   temporary-audio recovery lifecycle is S-04-02 and nothing here anticipates
 *   it; `recordingInProgress` is one boolean and carries no audio (D-019 J-1).
 *
 *   NO AUTH MATERIAL. No access token, refresh token, session, email or
 *   password. The envelope has a closed shape and a test asserts the stored
 *   record's keys against it.
 *
 *   No mic timer, no elapsed time, no warning flag, no keep-awake state, no
 *   operation token, no in-flight latch, no loading flag, no keyboard state,
 *   no RecorderPort state, no STT request. Those are transient UI facts; a
 *   restored timer would be a lie about a recording that no longer exists.
 *
 * ONE ACTIVE DRAFT PER USER, and that is the CURRENT product model rather than
 * a new one. The Capture screen has always held exactly one draft at a time —
 * there is one screen, one note, one idempotency key — so one durable slot is
 * the faithful durable version of it. Nothing here invents simultaneous
 * drafts, and the slot is keyed by the authenticated user id so User A's draft
 * is not merely hidden from User B but is stored under a key User B never
 * reads.
 *
 * THE OWNER IS CHECKED ON READ, NOT ONLY ON KEY CHOICE. `read()` takes the
 * owner it expects and refuses a record whose stored `ownerUserId` does not
 * match, so a key collision, a corrupted record or a future key-scheme change
 * cannot leak one user's words to another. Belt and braces, because this is
 * the failure that would be worst.
 *
 * VERSIONED, AND STRICT ABOUT IT. `schemaVersion` is written on every record
 * and any other value is treated as absent — never migrated in place, never
 * partially read. An unknown version is left on disk rather than deleted: a
 * newer Crewlink may understand it, and deleting Brandon's words because this
 * build does not recognise them is the one outcome worse than not offering
 * them.
 */

/**
 * **VERSION 2 — M4 / S-04-02** added `pendingAudioId`.
 *
 * Version 1 is READ AND UPGRADED rather than discarded, and that exception is
 * deliberate: version 1 is the envelope Crewlink is running on Jerry's phone
 * right now, so "ignore what you do not recognise" would mean an app update
 * silently eating whatever unfinished Capture he happened to be holding. The
 * upgrade is trivial and total — a v1 draft had no audio pointer, so it gets
 * `pendingAudioId: null`, which is exactly true. Any OTHER version is still
 * ignored and left on disk.
 */
export const CAPTURE_DRAFT_SCHEMA_VERSION = 2;

/** Versions this build can read. v1 upgrades in memory; nothing is rewritten on read. */
const READABLE_DRAFT_SCHEMA_VERSIONS = [1, 2];

/** One slot per authenticated user. The owner id is the key. */
export const CAPTURE_DRAFT_KEY_PREFIX = 'capture-draft:';

export function captureDraftKey(ownerUserId: string): string {
  return `${CAPTURE_DRAFT_KEY_PREFIX}${ownerUserId}`;
}

/**
 * Where the draft is in its life.
 *
 * `editing`      — local work in progress. Nothing has been dispatched.
 * `pending-save` — a Save was dispatched and the server's answer is not known
 *                  on this device. The frozen payload is carried with it, and
 *                  the product wording for this state is **"Waiting to send"**
 *                  (D-002, PT §20) — never "Saved", never "Failed".
 */
export type CaptureDraftLifecycle = 'editing' | 'pending-save';

/**
 * The EXACT payload that was dispatched, frozen.
 *
 * Structurally a `CreateCaptureInput` (status required rather than optional),
 * so it is handed straight back to the writer on retry. Storing it — rather
 * than rebuilding it from restored state — is what makes "the retry sends the
 * same Capture" a property instead of a hope: same `client_capture_uuid`, same
 * text, same status, same timezone, so the server's owner-scoped idempotency
 * returns the ORIGINAL Capture if the first request landed, and creates
 * exactly one if it did not.
 */
export interface CaptureDraftPendingPayload {
  readonly projectId: string;
  readonly clientCaptureUuid: string;
  readonly transcript: string | null;
  readonly timezone: string;
  readonly status: CaptureStatus;
}

export interface CaptureDraft {
  readonly schemaVersion: number;
  readonly ownerUserId: string;
  readonly projectId: string;
  /**
   * Stored so the recovery card can name the Project without a network read.
   * The card is shown at cold launch, possibly with no signal, and a recovery
   * prompt that cannot say WHICH job it belongs to is not a prompt Brandon can
   * act on.
   */
  readonly projectName: string;
  readonly clientCaptureUuid: string;
  /** The Final Accepted Transcript candidate — typed, spoken, or edited. Restored verbatim. */
  readonly note: string;
  readonly status: CaptureStatus;
  readonly lifecycle: CaptureDraftLifecycle;
  /**
   * True while a recording existed on the device and had not yet become text.
   *
   * ONE BOOLEAN, NO AUDIO (D-019 J-1). Its only job is to let the relaunched
   * app say the honest sentence — a recording was in progress and could not be
   * kept — instead of silently showing a draft with a mysteriously missing
   * half. Jerry decided against partial-audio recovery, so this marker must
   * never be read as a promise that the audio is recoverable: it is a record
   * that something was lost, which is the opposite claim.
   */
  readonly recordingInProgress: boolean;
  /**
   * **THE AUTHORITY ON UNRESOLVED AUDIO — M4 / S-04-02.**
   *
   * Names the safely finalized recording that has not yet become a transcript,
   * or `null`. An id rather than a boolean because one Capture may record,
   * transcribe and record again while keeping the same `client_capture_uuid` —
   * only an id can tell the second recording from the first.
   *
   * **Nothing else decides whether audio is unresolved.** Not the presence of a
   * Blob on disk, not the note text, not `recordingInProgress`, not age. This
   * field, matched against the record it names, is the whole answer — which is
   * what makes clearing it (in the same write as the transcript) the moment the
   * audio becomes inert, whether or not the delete that follows succeeds.
   */
  readonly pendingAudioId: string | null;
  readonly createdAtMs: number;
  readonly updatedAtMs: number;
  /** Present exactly when `lifecycle` is `pending-save`. */
  readonly pendingPayload: CaptureDraftPendingPayload | null;
}

/**
 * The closed key set of the envelope. Exported so the security test can assert
 * that a stored record contains these keys and NOTHING else — the automated
 * half of "no auth material is ever written here".
 */
export const CAPTURE_DRAFT_FIELDS = [
  'schemaVersion',
  'ownerUserId',
  'projectId',
  'projectName',
  'clientCaptureUuid',
  'note',
  'status',
  'lifecycle',
  'recordingInProgress',
  'pendingAudioId',
  'createdAtMs',
  'updatedAtMs',
  'pendingPayload',
] as const;

/**
 * Exhaustive by construction: a fourth Capture status added to the union
 * without a line here is a COMPILE error, not a value that silently fails
 * validation and throws a draft away.
 */
const CAPTURE_STATUS_GUARD: Record<CaptureStatus, true> = {
  open: true,
  repaired: true,
  closed: true,
};

function isCaptureStatus(value: unknown): value is CaptureStatus {
  return typeof value === 'string' && Object.hasOwn(CAPTURE_STATUS_GUARD, value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function parsePendingPayload(value: unknown): CaptureDraftPendingPayload | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  if (!isNonEmptyString(raw.projectId)) return null;
  if (!isNonEmptyString(raw.clientCaptureUuid)) return null;
  if (!isNonEmptyString(raw.timezone)) return null;
  if (!isCaptureStatus(raw.status)) return null;
  if (raw.transcript !== null && typeof raw.transcript !== 'string') return null;

  return {
    projectId: raw.projectId,
    clientCaptureUuid: raw.clientCaptureUuid,
    transcript: raw.transcript,
    timezone: raw.timezone,
    status: raw.status,
  };
}

/**
 * A stored record, validated field by field, or `null`.
 *
 * STRICT ON PURPOSE, in both directions. A record that is the wrong schema
 * version, belongs to another owner, or is missing any required field is not
 * partially restored and not repaired — it is treated as absent. Half a draft
 * presented as Brandon's words would be worse than no draft at all, and a
 * `pending-save` record without its frozen payload is exactly the shape that
 * could cause a second Capture, so it is refused outright.
 */
export function parseCaptureDraft(
  value: unknown,
  expectedOwnerUserId: string,
): CaptureDraft | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;

  if (typeof raw.schemaVersion !== 'number') return null;
  if (!READABLE_DRAFT_SCHEMA_VERSIONS.includes(raw.schemaVersion)) return null;
  if (!isNonEmptyString(raw.ownerUserId)) return null;
  // The owner check, restated where it actually protects: a record is only
  // ever handed back to the identity it was written under.
  if (raw.ownerUserId !== expectedOwnerUserId) return null;
  if (!isNonEmptyString(raw.projectId)) return null;
  if (!isNonEmptyString(raw.projectName)) return null;
  if (!isNonEmptyString(raw.clientCaptureUuid)) return null;
  if (typeof raw.note !== 'string') return null;
  if (!isCaptureStatus(raw.status)) return null;
  if (raw.lifecycle !== 'editing' && raw.lifecycle !== 'pending-save') return null;
  if (typeof raw.recordingInProgress !== 'boolean') return null;
  // Absent in a version 1 record, and absent means exactly what it says: that
  // draft predates temporary audio recovery, so it points at no recording.
  // A present-but-wrong-typed value is a corrupt record, not a v1 one.
  const pendingAudioId: string | null =
    raw.pendingAudioId === undefined || raw.pendingAudioId === null
      ? null
      : isNonEmptyString(raw.pendingAudioId)
        ? raw.pendingAudioId
        : null;
  if (raw.pendingAudioId !== undefined && raw.pendingAudioId !== null && pendingAudioId === null) {
    return null;
  }
  if (!isFiniteNumber(raw.createdAtMs)) return null;
  if (!isFiniteNumber(raw.updatedAtMs)) return null;

  const pendingPayload =
    raw.pendingPayload == null ? null : parsePendingPayload(raw.pendingPayload);
  if (raw.lifecycle === 'pending-save' && pendingPayload === null) return null;

  return {
    schemaVersion: CAPTURE_DRAFT_SCHEMA_VERSION,
    ownerUserId: raw.ownerUserId,
    projectId: raw.projectId,
    projectName: raw.projectName,
    clientCaptureUuid: raw.clientCaptureUuid,
    note: raw.note,
    status: raw.status,
    lifecycle: raw.lifecycle,
    recordingInProgress: raw.recordingInProgress,
    pendingAudioId,
    createdAtMs: raw.createdAtMs,
    updatedAtMs: raw.updatedAtMs,
    pendingPayload,
  };
}

/**
 * Whether this draft is worth offering back.
 *
 * A draft with nothing in it is not a recovery prompt, it is noise — and noise
 * on the launch screen is how a real prompt gets ignored. Three things make a
 * draft worth showing: words to restore, a Save whose fate is unknown, or a
 * recording that was lost and must be admitted to.
 *
 * NOTE the deliberate omission: a status the user changed but never typed
 * against is not, by itself, recoverable work. Restoring "REPAIRED" over an
 * empty note would offer Brandon a Capture he never made.
 */
export function isRecoverableCaptureDraft(draft: CaptureDraft): boolean {
  return (
    draft.lifecycle === 'pending-save' ||
    draft.recordingInProgress ||
    // S-04-02: a draft with no words at all is still worth offering when it
    // points at a finished recording — the recording IS the work.
    draft.pendingAudioId !== null ||
    draft.note.trim().length > 0
  );
}

/**
 * Roughly how old, in the plain words the card uses.
 *
 * RELATIVE, NEVER ABSOLUTE, and that is an INV-T17 decision rather than a
 * style one: an absolute time would have to be rendered in a zone, the
 * canonical Crewlink zone may not be loaded at cold launch, and the device
 * zone is exactly what INV-T17 forbids substituting. "About 2 hours ago" needs
 * no zone to be true.
 */
export function formatCaptureDraftAge(updatedAtMs: number, nowMs: number): string {
  const elapsedMs = Math.max(0, nowMs - updatedAtMs);
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${String(minutes)} minutes ago`;

  const hours = Math.floor(minutes / 60);
  if (hours === 1) return 'about 1 hour ago';
  if (hours < 24) return `about ${String(hours)} hours ago`;

  const days = Math.floor(hours / 24);
  return days === 1 ? 'about 1 day ago' : `about ${String(days)} days ago`;
}

export interface CaptureDraftStore {
  /** The signed-in user's draft, or `null`. Never throws; never returns another user's. */
  read(ownerUserId: string): Promise<CaptureDraft | null>;
  /** Resolves once the draft is COMMITTED to durable storage. Never throws. */
  write(draft: CaptureDraft): Promise<void>;
  /** Removes this user's draft. Never throws. */
  clear(ownerUserId: string): Promise<void>;
  /**
   * Whether the underlying store can actually survive the app closing —
   * M4 / S-04-03, closing I-036.
   *
   * **Read it AFTER an operation has resolved**, never before. The IndexedDB
   * adapter cannot know it has failed until its open settles, so an early read
   * is optimistic by construction. The Capture screen asks only once its first
   * draft read has come back, which is the point at which the answer is real.
   *
   * It exists so the product can SAY something true. Until now a runtime with
   * no working storage behaved normally, saved normally, and simply never
   * offered anything back — the recovery guarantee silently absent, with
   * nobody told.
   */
  readonly durable: boolean;
}

export function createCaptureDraftStore(store: LocalStorePort): CaptureDraftStore {
  return {
    // A getter, not a snapshot: the adapter's answer changes when its open
    // fails, and a value copied at construction would freeze the optimistic
    // one forever.
    get durable() {
      return store.durable;
    },

    async read(ownerUserId) {
      // No identity, no read. "We could not tell who this is" must never
      // resolve to somebody's words (S-04-01 user-scoping rule).
      if (!isNonEmptyString(ownerUserId)) return null;
      const record = await store.get(captureDraftKey(ownerUserId));
      return parseCaptureDraft(record, ownerUserId);
    },

    async write(draft) {
      if (!isNonEmptyString(draft.ownerUserId)) return;
      // Written field by field rather than by spreading the argument: a
      // caller cannot smuggle an extra property — a token, a blob, a whole
      // component state — into durable storage by attaching it to the object.
      // The envelope is closed HERE, at the boundary, where it can be enforced.
      const record: Record<(typeof CAPTURE_DRAFT_FIELDS)[number], unknown> = {
        schemaVersion: CAPTURE_DRAFT_SCHEMA_VERSION,
        ownerUserId: draft.ownerUserId,
        projectId: draft.projectId,
        projectName: draft.projectName,
        clientCaptureUuid: draft.clientCaptureUuid,
        note: draft.note,
        status: draft.status,
        lifecycle: draft.lifecycle,
        recordingInProgress: draft.recordingInProgress,
        pendingAudioId: draft.pendingAudioId,
        createdAtMs: draft.createdAtMs,
        updatedAtMs: draft.updatedAtMs,
        pendingPayload:
          draft.lifecycle === 'pending-save' && draft.pendingPayload
            ? {
                projectId: draft.pendingPayload.projectId,
                clientCaptureUuid: draft.pendingPayload.clientCaptureUuid,
                transcript: draft.pendingPayload.transcript,
                timezone: draft.pendingPayload.timezone,
                status: draft.pendingPayload.status,
              }
            : null,
      };
      await store.set(captureDraftKey(draft.ownerUserId), record);
    },

    async clear(ownerUserId) {
      if (!isNonEmptyString(ownerUserId)) return;
      await store.remove(captureDraftKey(ownerUserId));
    },
  };
}

/**
 * The application's store, created once at first use.
 *
 * Lazy rather than at module load so importing this file — which the auth
 * feature does, for the sign-out purge — never opens a database as a side
 * effect of an import.
 */
let applicationStore: CaptureDraftStore | null = null;

export function getCaptureDraftStore(): CaptureDraftStore {
  applicationStore ??= createCaptureDraftStore(selectLocalStore());
  return applicationStore;
}
